import { SessionBoundary } from './context/SessionBoundary';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';
import { App } from './App';
import { ToastProvider, ScreenReaderAnnouncerProvider } from './components/ui';
import { AuthProvider, TenantProvider } from './context';
import './styles/design-system.css';
import './styles/app-components.css';
import './styles/mobile-responsive.css';
import './styles/tour.css';
import './styles/calm-experience.css';

// ============================================
// SENTRY ERROR MONITORING
// ============================================

// Monitoring is deliberately disabled until a separately reviewed opt-in configuration.
// No replay, journal/chat breadcrumbs, or device telemetry are sent before consent.
// ============================================
// SERVICE WORKER
// ============================================

function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
        });

        console.log('[App] Service worker registered:', registration.scope);

        // Check for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New content is available, prompt user to refresh
                console.log('[App] New content available, please refresh.');
              }
            });
          }
        });
      } catch (error) {
        console.log('[App] Service worker registration failed:', error);
      }
    });
  }
}

// Initialize service worker
if (import.meta.env.PROD) registerServiceWorker();

// ============================================
// APP INITIALIZATION
// ============================================

const root = document.getElementById('root');

if (!root) {
  throw new Error('Root element not found');
}

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <TenantProvider>
          <AuthProvider>
            <SessionBoundary>
              <ScreenReaderAnnouncerProvider>
                <ToastProvider>
                  <App />
                </ToastProvider>
              </ScreenReaderAnnouncerProvider>
            </SessionBoundary>
          </AuthProvider>
        </TenantProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>
);

